import React, { useState, useRef, useEffect } from "react";
import {
  Box, Button, Input, Flex, Text, VStack, Select, FormControl
} from "@chakra-ui/react";
import PageHeading from "../../components/PageHeading";
import ColorPicker from "../../components/ColorPicker";

const ImageGenerator = () => {
  const [width, setWidth] = useState(300);
  const [height, setHeight] = useState(200);
  const [bgColor, setBgColor] = useState("#B4B4B4");
  const [textColor, setTextColor] = useState("#ffffff");
  const [mainText, setMainText] = useState("Sample");
  const [subText, setSubText] = useState("{W} x {H}");
  const [fileFormat, setFileFormat] = useState("png");
  const [mainTextSize, setMainTextSize] = useState(24);
  const [subTextSize, setSubTextSize] = useState(18);
  const [displayWidth, setDisplayWidth] = useState(400);
  const [displayHeight, setDisplayHeight] = useState(400);
  const [maxDisplayWidth, setMaxDisplayWidth] = useState(400);
  const maxDisplayHeight = 500;
  const canvasRef = useRef(null);

  useEffect(() => {
    const updateMaxWidth = () => {
      if (window.innerWidth < 500) {
        setMaxDisplayWidth(300);
      } else if (window.innerWidth < 768) {
        setMaxDisplayWidth(400);
      } else {
        setMaxDisplayWidth(window.innerWidth * 0.5);
      }
    };

    updateMaxWidth();
    window.addEventListener("resize", updateMaxWidth);
    return () => window.removeEventListener("resize", updateMaxWidth);
  }, []);

  useEffect(() => {
    let scale = Math.min(maxDisplayWidth / width, maxDisplayHeight / height, 1);
    setDisplayHeight(height * scale);
    setDisplayWidth(width * scale);
  }, [maxDisplayWidth, width, height]);

  const generateImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    // Draw background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    // Draw main text
    ctx.fillStyle = textColor;
    ctx.font = `bold ${mainTextSize}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(mainText.replace("{W}", width).replace("{H}", height), width / 2, height / 2 - 10);

    // Draw subtext
    ctx.font = `${subTextSize}px Arial`;
    ctx.fillText(subText.replace("{W}", width).replace("{H}", height), width / 2, height / 2 + mainTextSize - 10);
  };

  useEffect(() => {
    generateImage();
  }, [width, height, bgColor, textColor, mainText, subText, mainTextSize, subTextSize]);

  const downloadImage = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = `generated-image.${fileFormat}`;
    link.href = canvas.toDataURL(`image/${fileFormat}`);
    link.click();
  };

  return (
    <VStack spacing={4} p={5}>
      <PageHeading
        mainText="Dummy Image Generator"
        subText="Generate dummy sample images with your desired size and color"
      />

      <Flex direction={{ base: "column", md: "row" }} gap={8} justifyContent={"start"} mt={"12px"} width={"100%"}>
        <Box flex={1} overflow="hidden" display={"flex"} justifyContent={"center"} alignItems={"center"}>
          <canvas ref={canvasRef} style={{ width: displayWidth, height: displayHeight }} />
        </Box>

        <VStack align="start" minWidth="300px" maxWidth="350px" gap={4}>

          {/* Width & Height */}
          <FormControl>
            <Text>Image Dimensions</Text>
            <Flex gap={3}>
              <Input type="number" value={width} onChange={(e) => setWidth(+e.target.value)} placeholder="Width" />
              <Input type="number" value={height} onChange={(e) => setHeight(+e.target.value)} placeholder="Height" />
            </Flex>
          </FormControl>

          {/* Background & Text Color */}
          <FormControl>
            <Flex gap={3}>
              <VStack align={"start"} gap={0}>
                <Text>Background</Text>
                <ColorPicker value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
              </VStack>
              <VStack align={"start"} gap={0}>
                <Text>Text</Text>
                <ColorPicker value={textColor} onChange={(e) => setTextColor(e.target.value)} />
              </VStack>
            </Flex>
          </FormControl>

          {/* Main Text */}
          <FormControl>
            <Text>Main Text</Text>
            <Input
              placeholder="Main Text (default: Sample)"
              value={mainText}
              onChange={(e) => setMainText(e.target.value)}
            />
          </FormControl>

          {/* Subtext */}
          <FormControl>
            <Text>Subtext</Text>
            <Input
              placeholder="Subtext (default: {W} x {H})"
              value={subText}
              onChange={(e) => setSubText(e.target.value)}
            />
          </FormControl>

          {/* Main Text Size */}
          <Flex gap={3} width={"100%"}>
            <FormControl flex="1">
              <Text>Main Text Size</Text>
              <Select value={mainTextSize} onChange={(e) => setMainTextSize(+e.target.value)}>
                <option value="16">16px</option>
                <option value="18">18px</option>
                <option value="20">20px</option>
                <option value="24">24px</option>
                <option value="30">30px</option>
                <option value="36">36px</option>
                <option value="48">48px</option>
              </Select>
            </FormControl>

            {/* Subtext Size */}
            <FormControl  flex="1">
              <Text>Subtext Size</Text>
              <Select value={subTextSize} onChange={(e) => setSubTextSize(+e.target.value)}>
                <option value="12">12px</option>
                <option value="14">14px</option>
                <option value="16">16px</option>
                <option value="18">18px</option>
                <option value="22">22px</option>
                <option value="26">26px</option>
                <option value="32">32px</option>
              </Select>
            </FormControl>
          </Flex>

          {/* File Format */}
          <FormControl>
            <Text>File Format</Text>
            <Select value={fileFormat} onChange={(e) => setFileFormat(e.target.value)}>
              <option value="png">PNG (.png)</option>
              <option value="jpg">JPEG (.jpg)</option>
              <option value="webp">WebP (.webp)</option>
              <option value="bmp">BMP (.bmp)</option>
              <option value="gif">GIF (.gif)</option>
            </Select>
          </FormControl>

          <Button onClick={downloadImage}>Download Image</Button>
        </VStack>
      </Flex>
    </VStack>
  );
};

export default ImageGenerator;
